import { DEFAULT_COUNTRY_PHONE_CODE, SESSION_PHONE_NUMBER } from '../../../constants';
import { getFullFormattedNumber, getStorageItem, setStorageItem } from 'utils';
import type { Sender } from 'interfaces/connectionType';

/** Канал первой отправки OTP с PhoneView; не перезаписывается при resend. */
export type InitialOtpSender = Extract<Sender, 'sms' | 'whatsapp'>;

export interface SessionPhoneStorage {
  value?: string;
  valuemx?: string;
  new?: boolean;
  sender?: Sender;
  initial_sender?: InitialOtpSender;
  /** Первая отправка OTP (type: auth) не прошла — resend должен идти как auth. */
  is_pending?: boolean;
}

const isValidSender = (value: unknown): value is Sender => {
  return value === 'sms' || value === 'whatsapp' || value === 'call-verification';
};

const isValidInitialSender = (value: unknown): value is InitialOtpSender => {
  return value === 'sms' || value === 'whatsapp';
};

/**
 * Сужает поля снимка `s_phn` после JSON.parse: неизвестные `sender` / `initial_sender` отбрасываются,
 * чтобы UI и API не опирались на мусор в localStorage.
 */
export const sanitizeSessionPhoneStorage = (data: unknown): SessionPhoneStorage => {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {};
  }

  const o = data as Record<string, unknown>;
  const out: SessionPhoneStorage = {};

  if (typeof o.value === 'string') {
    out.value = o.value;
  }

  if (typeof o.valuemx === 'string') {
    out.valuemx = o.valuemx;
  }

  if (o.new === true) {
    out.new = true;
  }

  if (isValidSender(o.sender)) {
    out.sender = o.sender;
  }

  if (isValidInitialSender(o.initial_sender)) {
    out.initial_sender = o.initial_sender;
  }

  if (o.is_pending === true) {
    out.is_pending = true;
  }

  return out;
};

export const normalizeSessionPhoneKey = (phone: string): string => {
  let normalized = phone;

  if (normalized.startsWith(DEFAULT_COUNTRY_PHONE_CODE)) {
    normalized = normalized.substring(DEFAULT_COUNTRY_PHONE_CODE.length);
  }

  return normalized.replace(/\s/g, '');
};

/** Парсинг сырой строки из storage; при ошибке или не-объекте возвращает `{}`. */
export const parseSessionPhoneRaw = (raw: string): SessionPhoneStorage => {
  try {
    const parsed: unknown = JSON.parse(raw);
    return sanitizeSessionPhoneStorage(parsed);
  } catch {
    return {};
  }
};

export const toInitialOtpSender = (channel: Sender): InitialOtpSender | undefined => {
  if (channel === 'sms' || channel === 'whatsapp') {
    return channel;
  }

  return undefined;
};

export const parseSessionPhoneStorage = (): SessionPhoneStorage | null => {
  const raw = getStorageItem(SESSION_PHONE_NUMBER);
  if (!raw) {
    return null;
  }

  return parseSessionPhoneRaw(raw);
};

export const getStoredOtpSender = (): Sender | undefined => {
  return parseSessionPhoneStorage()?.sender;
};

export const getStoredInitialOtpSender = (): InitialOtpSender | undefined => {
  return parseSessionPhoneStorage()?.initial_sender;
};

interface InitialSenderResendVisibility {
  resendOptionsUnlocked: boolean;
}

/** Скрывает канал первой отправки с PhoneView, пока активен тот же sender в storage. */
export const shouldShowOtpResendChannel = (
  channel: InitialOtpSender,
  { resendOptionsUnlocked }: Pick<InitialSenderResendVisibility, 'resendOptionsUnlocked'>,
): boolean => {
  if (resendOptionsUnlocked) {
    return true;
  }

  const initialChannel = getStoredInitialOtpSender();

  if (!initialChannel || channel !== initialChannel) {
    return true;
  }

  return getStoredOtpSender() !== initialChannel;
};

/** Альтернативный канал или звонок снимают ограничение на resend. */
export const unlocksOtpResendOptions = (sender: Sender): boolean => {
  const initialChannel = getStoredInitialOtpSender();
  if (!initialChannel) {
    return false;
  }

  if (sender === 'call-verification') {
    return true;
  }

  if (sender !== 'sms' && sender !== 'whatsapp') {
    return false;
  }

  return sender !== initialChannel;
};

export type UiOtpChannel = InitialOtpSender;

export const toUiOtpChannel = (channel: Sender | undefined): UiOtpChannel => {
  return channel === 'whatsapp' ? 'whatsapp' : 'sms';
};

export const formatOtpSenderLabel = (channel: UiOtpChannel): string => {
  return channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
};

/** Канал OTP для подписи в CodeView: storage → Redux → sms. */
export const resolveUiOtpSender = (otpSenderChannel?: string | null): UiOtpChannel => {
  const storedSender = getStoredOtpSender();

  if (storedSender === 'sms' || storedSender === 'whatsapp' || storedSender === 'call-verification') {
    return toUiOtpChannel(storedSender);
  }

  if (otpSenderChannel === 'sms' || otpSenderChannel === 'whatsapp') {
    return otpSenderChannel;
  }

  return 'sms';
};

/** Номер для CodeView: маска дубликата (`valuemx`) приоритетнее prop `phone` (`value`). */
export const getCodeViewDisplayPhone = (phoneProp: string): string => {
  return parseSessionPhoneStorage()?.valuemx ?? phoneProp;
};

/** Ключ `otp_remaining_state`: всегда `s_phn.value`, не маска `valuemx`. */
export const getOtpLimitsPhoneKey = (fallbackPhone?: string | null): string | null => {
  const session = parseSessionPhoneStorage();

  if (session?.value) {
    return getFullFormattedNumber(session.value);
  }

  return fallbackPhone ?? null;
};

/** Номер для UI после авторизации (маска дубликата приоритетнее). */
export const getAuthorizedSessionPhone = (): string | undefined => {
  const session = parseSessionPhoneStorage();

  if (!session?.value && !session?.valuemx) {
    return undefined;
  }

  return session.valuemx ?? session.value;
};

/** Duplicate origin login stores masked phone in `valuemx`; TTL does not apply. */
export const isDuplicateLoginSession = (): boolean => {
  return Boolean(parseSessionPhoneStorage()?.valuemx);
};

/** Номер из сессии для неавторизованного входа (только `value`). */
export const getLoginSessionPhone = (): SessionPhoneStorage | null => {
  const session = parseSessionPhoneStorage();

  if (!session?.value) {
    return null;
  }

  return session;
};

/** Merges `sender` into existing session phone JSON (resend flows that do not use `usePhoneSubmission`). */
export const patchSessionPhoneSender = (sender: Sender): void => {
  const current = parseSessionPhoneStorage();
  if (!current?.value && !current?.valuemx) {
    return;
  }

  setStorageItem(SESSION_PHONE_NUMBER, JSON.stringify({ ...current, sender }));
};

/** Сессия при 429 на request-otp с PhoneView: pending + sender, без initial_sender. */
export const markOtpAuthRateLimitSession = (phone: string, channel: InitialOtpSender): void => {
  const current = parseSessionPhoneStorage() ?? {};

  setStorageItem(
    SESSION_PHONE_NUMBER,
    JSON.stringify({
      ...current,
      value: normalizeSessionPhoneKey(phone),
      sender: channel,
      is_pending: true,
    }),
  );
};

export const markPendingRequestOtp = (phone: string): void => {
  const current = parseSessionPhoneStorage() ?? {};

  setStorageItem(
    SESSION_PHONE_NUMBER,
    JSON.stringify({
      ...current,
      value: normalizeSessionPhoneKey(phone),
      is_pending: true,
    }),
  );
};

export const hasPendingRequestOtp = (phone: string): boolean => {
  const session = parseSessionPhoneStorage();

  if (!session?.value || session.is_pending !== true) {
    return false;
  }

  return normalizeSessionPhoneKey(session.value) === normalizeSessionPhoneKey(phone);
};

export const clearPendingRequestOtp = (): void => {
  const current = parseSessionPhoneStorage();

  if (!current?.is_pending) {
    return;
  }

  const { is_pending: _pending, ...rest } = current;

  setStorageItem(SESSION_PHONE_NUMBER, JSON.stringify(rest));
};
